// Talking to a hub that issues its own sessions.
//
// The whole surface is three calls, and none of them touch a token. The server
// sets `at` as an HttpOnly cookie, which means this module CANNOT read it and
// deliberately never tries: "am I signed in" is answered by asking the server,
// not by inspecting local state. Mirroring the answer into something readable —
// a flag in localStorage, a second non-HttpOnly cookie — is how a UI ends up
// confidently rendering a signed-in shell for someone whose every request 401s.
import { getConfig } from '../config/runtimeConfig';
import { HttpError } from '../lib/httpError';
import { clearUploadStorage } from '../features/upload/storageKeys';

/** Which product this deployment is. Decided by the server, read once at boot. */
export type Edition = 'enterprise' | 'community';

export interface SessionUser {
  userId: string | null;
  email: string | null;
  displayName: string | null;
  /** The two halves the profile screen edits; displayName is them joined. */
  givenName?: string | null;
  familyName?: string | null;
  /** False for an account that signs in through an identity provider. */
  hasPassword?: boolean;
  /** Account preferences the app acts on; `openViewerNewTab` is the one that ships. */
  prefs?: Record<string, unknown>;
  superadmin: boolean;
  /**
   * Why this account cannot upload, or null when it can.
   *
   * A sentence written for the person reading it — "choose which workspace your
   * uploads go to" — so the UI shows it rather than paraphrasing. Optional
   * because a deployment can be older than the field.
   */
  writeDenied?: string | null;
}

/** The API origin. Same as the page's in every deployment, but not by accident. */
function apiBase(): string {
  return getConfig().apiRoot;
}

/**
 * The current session, or null when there isn't one.
 *
 * 401 is an ANSWER here, not a failure — the global auth hook refuses anything
 * it cannot authenticate, so this route needs no logic of its own and a 401 is
 * exactly the information the caller wants. A network error is different and
 * must not be flattened into "signed out", or an offline blip would throw the
 * user back to a login form they cannot complete.
 */
export async function currentSession(): Promise<SessionUser | null> {
  const res = await fetch(`${apiBase()}/auth/session`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new HttpError(`session check failed: ${res.status}`, res.status);
  return (await res.json()) as SessionUser;
}

/**
 * A session check made ahead of when it is needed (at boot, alongside the
 * edition). Null when it could not be put — the server unreachable or failing —
 * which is not an answer, so the caller asks again; otherwise what the server
 * said, a signed-out 401 included.
 */
export type SessionProbe = Promise<{ me: SessionUser | null } | null>;

export function probeSession(): SessionProbe {
  return currentSession().then((me) => ({ me }), () => null);
}

export class LoginError extends Error {}

/**
 * What the sign-in screen needs before anyone has signed in.
 *
 * Read at RUNTIME rather than taken from the deployed config, because whether
 * a deployment accepts new accounts is a setting an administrator can change
 * this afternoon — a value baked in at `cdk deploy` would keep offering a
 * "create an account" link hours after somebody closed registration, and the
 * only feedback would be a 403 after filling in the form.
 *
 * Failure is not an answer. If this cannot be reached, the caller falls back to
 * not offering registration: a missing link is a smaller wrong than one that
 * leads nowhere.
 */
export async function authConfig(): Promise<{ signup: boolean; edition: Edition; google?: boolean }> {
  const res = await fetch(`${apiBase()}/auth/config`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new HttpError(`auth config failed: ${res.status}`, res.status);
  return (await res.json()) as { signup: boolean; edition: Edition };
}

/**
 * Exchange credentials for a session.
 *
 * The reply carries the token, and we ignore it: the same response sets the
 * HttpOnly cookie, and that is what authenticates the header-less requests the
 * viewer actually makes — <img> thumbnails and EventSource cannot carry an
 * Authorization header. Holding a second copy in JavaScript would add an XSS
 * target and buy nothing.
 */
export async function signIn(email: string, password: string): Promise<void> {
  const res = await fetch(`${apiBase()}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (res.ok) return;
  // The server answers an unknown address and a wrong password identically, on
  // purpose — so does this. Whatever it says is what the person sees; inventing
  // a friendlier message here would be inventing information we do not have.
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new LoginError(body.error || 'Could not sign in. Please try again.');
}

/**
 * How long any one sign-out request may take before we stop waiting for it.
 * Sign-out must FINISH: a request that hangs used to leave the page exactly as
 * it was, the menu closed and the person still signed in.
 */
const SIGN_OUT_REQUEST_MS = 5_000;

/** A fetch that gives up after `ms`, answering null for a timeout or a network failure. */
async function settled(fetchFn: typeof fetch, url: string, init: RequestInit, ms: number): Promise<Response | null> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), ms);
  try {
    return await fetchFn(url, { ...init, signal: ctl.signal });
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * End the session on the server, and make sure it stayed ended.
 *
 * The server renews a session on an ordinary response once it is past half its
 * life (server/src/auth/renewal.ts). A request already in flight when this runs
 * can come back with that renewal after the logout's cleared cookie, and the
 * browser keeps whichever cookie lands last: the reload would find the person
 * still signed in. So clear, ask whether a session remains, and clear again
 * while one does. A cookie that came back is a fresh one, which the check
 * itself cannot renew. Resolves false if a session is still answering after
 * `tries` rounds. Never throws, and no request is waited on for longer than
 * SIGN_OUT_REQUEST_MS.
 */
export async function endSession(fetchFn: typeof fetch = fetch, tries = 3, timeoutMs = SIGN_OUT_REQUEST_MS): Promise<boolean> {
  for (let i = 0; i < tries; i++) {
    await settled(fetchFn, `${apiBase()}/auth/logout`, { method: 'POST', credentials: 'include' }, timeoutMs);
    const check = await settled(fetchFn, `${apiBase()}/auth/session`, { credentials: 'include' }, timeoutMs);
    if (!check || check.status === 401) return true;
  }
  return false;
}

/** How long the page waits for the browser to empty its cache before moving on anyway. */
const CLEAR_CACHE_WAIT_MS = 2_000;

/**
 * Ask the browser to empty this site's HTTP cache, best-effort.
 *
 * The server answers with Clear-Site-Data: "cache" (server/src/http/local_auth.ts,
 * CLEAR_CACHE), and Chrome holds that response until it has walked its whole
 * cache — 15-25 s on an ordinary one. That is why it is its own request and
 * never the one that ends the session. It is given a moment, then left:
 * `keepalive` lets it outlive the page, but whether the browser finishes
 * clearing after the page has gone is up to the browser.
 */
export async function clearBrowserCache(fetchFn: typeof fetch = fetch, waitMs = CLEAR_CACHE_WAIT_MS): Promise<void> {
  const req = fetchFn(`${apiBase()}/auth/clear-cache`, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => null);
  await Promise.race([req, new Promise((r) => setTimeout(r, waitMs))]);
}

let signingOut: Promise<void> | null = null;

/**
 * End the session and reload.
 *
 * A full reload rather than clearing React state: the session gate runs at boot,
 * so reloading puts the app back through it and there is exactly one code path
 * that decides whether someone is signed in.
 *
 * Always reaches the reload — every step before it is bounded and none throws —
 * and a second click while it runs joins the first instead of starting over.
 */
export function signOut(): Promise<void> {
  signingOut ??= (async () => {
    await endSession();
    // The tab outlives the account: the persisted upload session and import
    // batches must not greet whoever signs in next.
    clearUploadStorage();
    await clearBrowserCache();
    window.location.replace(import.meta.env.BASE_URL);
  })();
  return signingOut;
}

/**
 * Ask for an account.
 *
 * 202 whatever happens, and the uniformity is the feature: a new address, one
 * already registered, and one registered but unverified all get the same answer,
 * so this screen cannot be used to find out who has an account here. The person
 * who owns the inbox learns the truth; anyone probing addresses learns nothing.
 * The UI must not undo that by saying anything more specific than the server did.
 */
export interface NewAccount {
  email: string;
  password: string;
  givenName: string;
  familyName: string;
  /** Present when accepting an invitation; it is what allows a closed deployment. */
  invite?: string;
}

export async function signUp(
  account: NewAccount,
): Promise<{ claimed: boolean; invited: boolean; workspace: string | null }> {
  // `claimed` means this was a fresh deployment with no owner, so the account
  // was created verified and administers it — there is no confirmation email
  // coming, and telling the person to go and look for one would send them to an
  // inbox that stays empty.
  //
  // `invited` means the same thing for a different reason: the invitation link
  // was mailed to this address, so control of it is already proved and no
  // second confirmation is sent. It is also what lets the account exist at all
  // on a deployment that has closed registration.
  //
  // `workspace` is the public id of the workspace an invitation joined, so the
  // screen can open it rather than the account's own.
  const body = await postJson<{ claimed?: boolean; invited?: boolean; workspace?: string }>('/auth/signup', {
    email: account.email,
    password: account.password,
    givenName: account.givenName,
    familyName: account.familyName,
    ...(account.invite ? { invite: account.invite } : {}),
  });
  return {
    claimed: body?.claimed === true,
    invited: body?.invited === true,
    workspace: typeof body?.workspace === 'string' ? body.workspace : null,
  };
}

export interface ProfileExtras {
  title?: string;
  institution?: string;
  linkedin?: string;
  roleTitle?: string;
}

/**
 * Change your own name (and, in the community profile, the lines under it).
 * The name halves may be left undefined to change only the extras.
 */
export async function updateProfile(
  givenName: string | undefined,
  familyName: string | undefined,
  extras: ProfileExtras = {},
): Promise<{ displayName: string; givenName: string; familyName: string }> {
  const res = await fetch(`${apiBase()}/auth/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...(givenName !== undefined || familyName !== undefined ? { givenName, familyName } : {}), ...extras }),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new HttpError(body.error || 'could not save your name', res.status);
  return body as { displayName: string; givenName: string; familyName: string };
}

/** Change your own password, proving you know the current one. */
export async function changePassword(current: string, next: string): Promise<void> {
  const res = await fetch(`${apiBase()}/auth/password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ current, next }),
  });
  if (res.status === 204) return;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new HttpError(body.error || 'could not change your password', res.status);
}

/** What an invitation is for, read without spending it. */
export async function readInvitation(
  token: string,
): Promise<{ email: string; workspace: string; role: string }> {
  // POST with the token in the BODY, not a query parameter: the server logs
  // req.url in full, and this token grants account creation on a deployment
  // that otherwise accepts none.
  const res = await fetch(`${apiBase()}/auth/invitation`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new HttpError(body.error || 'that invitation is no longer valid', res.status);
  return body as { email: string; workspace: string; role: string };
}

/** Ask for a reset code. 202 regardless, for the same reason signup is. */
export async function requestReset(email: string): Promise<void> {
  await postJson('/auth/reset/request', { email });
}

/**
 * One POST, one error convention.
 *
 * Every message the person sees comes from the server rather than from here.
 * These endpoints are carefully worded — identical answers for cases that must
 * stay indistinguishable, and specific ones where being specific is safe (a
 * password too short, a breached password, an expired link). Paraphrasing them
 * in the client is how that care gets lost.
 */
async function postJson<T = unknown>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Could not reach the server. Check your connection and try again.');
  }
  const parsed = (await res.json().catch(() => ({}))) as { error?: string };
  if (res.ok) return parsed as T;
  throw new LoginError(parsed.error || 'Something went wrong. Please try again.');
}

// ── codes, profile, preferences, account deletion ───────────────────────────

/** Who the person is: the profile screen's lines, kept out of the session. */
export interface Profile {
  email: string | null;
  displayName: string;
  givenName: string | null;
  familyName: string | null;
  title: string | null;
  institution: string | null;
  linkedin: string | null;
  roleTitle: string | null;
  hasPassword: boolean;
  hasGoogle: boolean;
}

export async function readProfile(): Promise<Profile> {
  const res = await fetch(`${apiBase()}/auth/profile`, { credentials: 'include' });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new HttpError(body.error || 'could not load your profile', res.status);
  return body as Profile;
}

/** Spend a six digit code sent to the address; signs the person in. */
export async function verifyCode(email: string, code: string): Promise<void> {
  await postJson('/auth/verify', { email, code });
}

/** Another code. 202 whatever the address. */
export async function resendCode(email: string, purpose: 'verify' | 'reset'): Promise<void> {
  await postJson('/auth/verify/resend', { email, purpose });
}

/** Spend a reset code and set the new password; signs the person in. */
export async function confirmResetCode(email: string, code: string, password: string): Promise<void> {
  await postJson('/auth/reset/confirm', { email, code, password });
}

export async function savePrefs(prefs: Record<string, boolean>): Promise<Record<string, unknown>> {
  const res = await fetch(`${apiBase()}/auth/prefs`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string; prefs?: Record<string, unknown> };
  if (!res.ok) throw new HttpError(body.error || 'could not save your settings', res.status);
  return body.prefs ?? {};
}

/** Delete your own account. The password is required when the account has one. */
export async function deleteAccount(password: string | undefined): Promise<void> {
  const res = await fetch(`${apiBase()}/auth/account`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(password ? { password } : {}),
  });
  if (res.status === 204) {
    await clearBrowserCache();
    return;
  }
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new HttpError(body.error || 'could not delete your account', res.status);
}

/** Where "Continue with Google" sends the browser. */
export function googleStartUrl(signup: boolean): string {
  return `${apiBase()}/auth/google/start${signup ? '?signup=1' : ''}`;
}

/**
 * The public id of the default collection of the caller's default workspace
 * (made if missing), or null when there is none to open. Where a new account
 * lands after sign-up.
 */
export async function defaultCollection(): Promise<string | null> {
  try {
    const res = await fetch(`${apiBase()}/collections/default`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { public_id?: string };
    return body.public_id ?? null;
  } catch {
    return null;
  }
}
